import { provideHttpClient } from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  isDevMode,
  provideZoneChangeDetection,
} from '@angular/core';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import {
  Auth,
  authState,
  connectAuthEmulator,
  provideAuth,
  initializeAuth,
  browserLocalPersistence,
  browserPopupRedirectResolver,
} from '@angular/fire/auth';
import { FIREBASE_OPTIONS } from '@angular/fire/compat';
import {
  connectFirestoreEmulator,
  getFirestore,
  provideFirestore,
} from '@angular/fire/firestore';
import {
  connectFunctionsEmulator,
  getFunctions,
  provideFunctions,
} from '@angular/fire/functions';
import {
  connectStorageEmulator,
  getStorage,
  provideStorage,
} from '@angular/fire/storage';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  Router,
  withComponentInputBinding,
} from '@angular/router';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import * as Sentry from '@sentry/angular';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { provideQuillConfig, QuillModule } from 'ngx-quill';
import { provideToastr } from 'ngx-toastr';
import { first, of, timeout } from 'rxjs';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { AuthService } from './services/auth.service';
import { ChannelService } from './services/channel.service';
import { CountryService } from './services/country.service';
import { EventService } from './services/event.service';
import { GroupService } from './services/group.service';
import { MemberService } from './services/member.service';
import { PrayerService } from './services/prayer.service';
import { UserService } from './services/user.service';
import { UtilsService } from './services/utils.service';
import { VideoService } from './services/video.service';
import { ArticleEffects } from './state/effects/article.effects';
import { ChannelEffects } from './state/effects/channel.effects';
import { EventEffects } from './state/effects/event.effects';
import { GroupEffects } from './state/effects/group.effects';
import { MemberEffects } from './state/effects/member.effects';
import { PodcastEffects } from './state/effects/podcast.effects';
import { PrayerEffects } from './state/effects/prayer.effects';
import { RouterEffects } from './state/effects/router.effects';
import { UserEffects } from './state/effects/user.effects';
import { VideoEffects } from './state/effects/video.effects';
import { reducers } from './state/reducers/reducers';

// import { USE_EMULATOR as USE_AUTH_EMULATOR } from '@angular/fire/compat/auth';
import {
  initializeAppCheck,
  provideAppCheck,
  ReCaptchaV3Provider,
} from '@angular/fire/app-check';
import { USE_EMULATOR as USE_FIRESTORE_EMULATOR } from '@angular/fire/compat/firestore';
import { USE_EMULATOR as USE_FUNCTIONS_EMULATOR } from '@angular/fire/compat/functions';
import { getAnalytics, provideAnalytics, ScreenTrackingService, UserTrackingService } from '@angular/fire/analytics';
import {
  connectDatabaseEmulator,
  getDatabase,
  provideDatabase,
} from '@angular/fire/database';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { provideServiceWorker } from '@angular/service-worker';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { AiService } from './services/ai.service';
import { AlbumService } from './services/album.service';
import { ConnectionService } from './services/connection.service';
import { ManagementService } from './services/management.service';
import { NoticeService } from './services/notice.service';
import { AlbumEffects } from './state/effects/album.effects';
import { AlbumImageEffects } from './state/effects/image.album.effects';
import { ManagementEffects } from './state/effects/management.effects';
import { NoticeEffects } from './state/effects/notice.effects';


export function initializeFirebase(auth: Auth) {
  return () =>
    authState(auth).pipe(
      timeout({ each: 2000, with: () => of(null) }),
      first()
    );
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppCheck(() => {
      if (!environment.production) {
        (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = (
          environment as any
        ).APP_CHECK_DEBUG_TOKEN;
      }
      const provider = new ReCaptchaV3Provider(environment.RECAPTCHA_SITE_KEY);
      return initializeAppCheck(undefined, {
        provider,
        isTokenAutoRefreshEnabled: true,
      });
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeFirebase,
      deps: [Auth],
      multi: true,
    },
    // {
    //   provide: USE_AUTH_EMULATOR,
    //   useValue: environment.useEmulators
    //     ? ['http://localhost:9099']
    //     : undefined,
    // },
    {
      provide: USE_FIRESTORE_EMULATOR,
      useValue: environment.useEmulators ? ['localhost', 8080] : undefined,
    },
    {
      provide: USE_FUNCTIONS_EMULATOR,
      useValue: environment.useEmulators ? ['localhost', 5001] : undefined,
    },
    importProvidersFrom(
      QuillModule.forRoot(),
      LoggerModule.forRoot({
        level: NgxLoggerLevel[environment.production ? 'ERROR' : 'DEBUG'],
        serverLogLevel:
          NgxLoggerLevel[environment.production ? 'OFF' : 'ERROR'],
        disableConsoleLogging: environment.production,
      }),
    ),
    provideQuillConfig({
      debug: 'error',
      theme: 'snow',
      // formats: ['bold', 'italic', 'underline', 'link', 'code-block'],
      modules: {
        toolbar: [
          ['bold', 'italic', 'underline', 'strike'],
          ['blockquote', 'code-block'],
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ indent: '-1' }, { indent: '+1' }],
          [{ size: ['small', false, 'large', 'huge'] }],
          [{ color: [] }, { background: [] }],
          [{ font: [] }],
          [{ align: [] }],
          ['clean'],
          ['link', 'image'],
        ],
      },
    }),
    provideCharts(withDefaultRegisterables()),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding()),
    AuthService,
    ConnectionService,
    GroupService,
    MemberService,
    PrayerService,
    EventService,
    NoticeService,
    ChannelService,
    VideoService,
    UserService,
    UtilsService,
    CountryService,
    AlbumService,
    AiService,
    provideStore(reducers, {
      runtimeChecks: {
        strictStateSerializability: false,
        strictActionSerializability: false,
      },
    }),
    provideRouterStore(),
    provideEffects(
      UserEffects,
      GroupEffects,
      MemberEffects,
      PrayerEffects,
      EventEffects,
      ChannelEffects,
      VideoEffects,
      PodcastEffects,
      ArticleEffects,
      RouterEffects,
      NoticeEffects,
      AlbumEffects,
      AlbumImageEffects,
      ManagementEffects,
      // PaginationEffects,
    ),
    ManagementService,

    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode(),
      connectInZone: true,
    }),
    provideHttpClient(),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAnalytics(() => getAnalytics()),
    // Only register automatic tracking listeners in production.
    // setAnalyticsCollectionEnabled(false) in AnalyticsService handles
    // suppressing events at the Firebase level, but skipping registration
    // entirely in dev avoids unnecessary router listener overhead.
    ...(environment.production ? [ScreenTrackingService, UserTrackingService] : []),
    provideMessaging(() => getMessaging()),
    provideDatabase(() => {
      const database = getDatabase();
      // Connection state (.info/connected) gates loading the user's groups
      if (environment.useEmulators) {
        connectDatabaseEmulator(database, '127.0.0.1', 9000);
      }
      return database;
    }),
    provideFirestore(() => {
      const firestore = getFirestore();
      if (environment.useEmulators) {
        connectFirestoreEmulator(firestore, 'localhost', 8080);
      }
      return firestore;
    }),
    provideAuth(() => {
      const auth = initializeAuth(getApp(), {
        persistence: browserLocalPersistence,
        popupRedirectResolver: browserPopupRedirectResolver,
      });
      if (environment.useEmulators) {
        connectAuthEmulator(auth, 'http://127.0.0.1:9099', {
          disableWarnings: true,
        });
      }
      return auth;
    }),
    provideStorage(() => {
      const fireStorage = getStorage();
      if (environment.useEmulators) {
        connectStorageEmulator(fireStorage, '127.0.0.1', 9199);
      }
      return fireStorage;
    }),
    provideFunctions(() => {
      const functions = getFunctions(getApp());
      if (environment.useEmulators) {
        connectFunctionsEmulator(functions, '127.0.0.1', 5001);
      }
      return functions;
    }),
    { provide: FIREBASE_OPTIONS, useValue: environment.firebase },
    // provideAnimationsAsync(),
    provideAnimations(),
    provideToastr(),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
